import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { makeStyles, Text, tokens } from '@fluentui/react-components';
import React, { useEffect } from 'react';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';
import PageBreadcrumb from './breadcrumb';
import PageContent from './contentbar';
import Headerbar from './headerbar';
import PageSidebar from './sidebar';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  bodyContainer: {
    display: 'flex',
    flex: 1,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  footer: {
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    width: '100%',
  },
  overlay: {
    '@media (max-width: 768px)': {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
      display: 'block',
    },
    display: 'none',
  },
});

const PageLayout: React.FC = () => {
  const styles = useStyles();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  const { instance, accounts } = useMsal();

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  // Initialization fallback: Only runs if userInfo wasn't already fetched in main.tsx
  // This acts as a safety net for edge cases or if initialization in main.tsx failed
  const hasInitializedRef = React.useRef(false);
  const userInfo = useConfigStore((state) => state.userConfig.userInfo);

  useEffect(() => {
    const initializeApp = async () => {
      const accountInfo = accounts[0] || null;

      // Skip if already initialized or if userInfo is already available
      if (!accountInfo || hasInitializedRef.current || userInfo) {
        return;
      }

      hasInitializedRef.current = true;

      try {
        const dataService = new DataService(instance, accountInfo);

        // Fallback: Fetch user info only if not already available
        console.log('Layout: Fallback user info fetch (main.tsx initialization may have failed)...');
        const userInfoData = await dataService.getUserInfo();
        console.log('Layout: User info fetched', userInfoData);

        // Fetch roles
        const userUpn = accountInfo.username;
        const rolesResponse = await dataService.getUserAppPermissions(userUpn, '2');

        let finalRoles = ['User'];
        if (rolesResponse && rolesResponse.length > 0) {
          const fetchedRoles = rolesResponse.map((perm: { rolle: string; appPart: string }) => perm.rolle);
          finalRoles = [...['User'], ...fetchedRoles];
          console.log('Layout: Roles fetched', finalRoles);

          const personalParts = rolesResponse.map((perm: { rolle: string; appPart: string }) => perm.appPart);
          sessionStorage.setItem('personalParts', JSON.stringify(personalParts));
        }

        sessionStorage.setItem('userPersonalRole', JSON.stringify(finalRoles));

        useConfigStore.setState({
          userConfig: {
            userInfo: userInfoData,
          },
          roleConfig: {
            availableRoles: finalRoles,
            selectedRole: finalRoles.length > 0 ? finalRoles[0] : null,
          },
        });

        console.log('Layout: Fallback initialization complete');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('Layout: Fallback initialization failed:', errorMessage);
      }
    };

    initializeApp();
  }, [accounts, instance, userInfo]);

  return (
    <div className={styles.root}>
      <Headerbar onMenuClick={toggleMobileSidebar} />
      <div className={styles.bodyContainer}>
        {isMobileSidebarOpen && (
          <div className={styles.overlay} onClick={closeMobileSidebar} />
        )}
        <PageSidebar
          isOpen={isMobileSidebarOpen}
          onClose={closeMobileSidebar}
        />
        <div className={styles.mainContent}>
          <PageBreadcrumb />
          <AuthenticatedTemplate>
            <PageContent />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <Text
              style={{
                fontSize: '1.5rem',
                marginLeft: '48px',
                marginTop: '48px',
                display: 'block',
              }}
            >
              Please log in to continue
            </Text>
          </UnauthenticatedTemplate>
        </div>
      </div>
      <footer className={styles.footer}>
        <Text>©{new Date().getFullYear()}</Text>
      </footer>
    </div>
  );
};

export default PageLayout;
